import type { FC } from "react";
import {
	ClientPortfolioInstructionHoldingTypeEnum,
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioPaymentFrequencyEnum,
	CurrencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { ToolIdentificationRow } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolIdentificationRow";
import { ToolSummaryRows } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolSummaryRows";
import { ToolTypeRow } from "web_myplann/Portfolio/Components/Redesign/Detail/FundDetail/ToolTypeRow";
import { toolHasActivePeriodicPurchaseInstructions } from "web_myplann/Portfolio/Helpers/activePurchaseInstructions";
import {
	type NormalizedTool,
	ToolKind,
} from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";

const PageResources = Resources.PortfolioDetail;

type Props = {
	tool: NormalizedTool;
	currencySign: string;
	totalValue: string;
	totalInvested: string;
	totalCount: number;
};

export const ToolFieldRows: FC<Props> = ({
	tool,
	currencySign,
	totalValue,
	totalInvested,
	totalCount,
}) => {
	const { t } = useResource();

	const toolType = tool.kind;
	const showRecurringPayment =
		toolHasActivePeriodicPurchaseInstructions(tool) && tool.monthlyPayment > 0;

	const monthlyRecurringValue = showRecurringPayment
		? `${moneyFormatter(
				tool.monthlyPayment,
				t(PageResources.SharedLabels.Currency[CurrencyEnum.CZK]),
				undefined,
				0,
			)} ${t(
				PageResources.ToolHistory.Fund.Frequency[
					ClientPortfolioInvestmentFrequencyEnum.Monthly
				],
			).toLowerCase()}`
		: null;

	if (toolType === ToolKind.Investment) {
		return (
			<>
				<ToolTypeRow tool={tool} />
				<ToolIdentificationRow tool={tool} />
				<ToolSummaryRows
					recurringValue={monthlyRecurringValue}
					totalInvested={totalInvested}
					totalValue={totalValue}
				/>
			</>
		);
	}

	if (toolType === ToolKind.Commodity) {
		return (
			<ToolSummaryRows
				recurringValue={
					showRecurringPayment
						? getPortfolioFrequencyValue(
								tool.monthlyPayment,
								currencySign,
								ClientPortfolioPaymentFrequencyEnum.Monthly,
								t,
								0,
							)
						: null
				}
				totalInvested={totalInvested}
				totalValue={totalValue}
			/>
		);
	}

	if (toolType === ToolKind.Crypto) {
		const item = tool.instructions[0]?.raw;
		if (!item) {
			return null;
		}
		return (
			<>
				<DetailRow
					label={t(PageResources.ToolHistory.Cryptocurrency.holdingType)}
					value={t(
						PageResources.ToolHistory.Cryptocurrency.HoldingType[
							item.holdingType ??
								ClientPortfolioInstructionHoldingTypeEnum.InAdministration
						],
					)}
				/>
				<DetailRow
					label={t(PageResources.ToolHistory.Cryptocurrency.amount)}
					value={totalCount}
				/>
				<ToolSummaryRows
					recurringValue={monthlyRecurringValue}
					totalInvested={totalInvested}
					totalValue={totalValue}
				/>
			</>
		);
	}

	if (toolType === ToolKind.BankProduct) {
		return (
			<>
				<ToolTypeRow tool={tool} />
				<ToolSummaryRows
					recurringValue={monthlyRecurringValue}
					totalInvested={totalInvested}
					totalValue={totalValue}
				/>
			</>
		);
	}

	return (
		<>
			<ToolTypeRow tool={tool} />
			<ToolIdentificationRow tool={tool} />
			<ToolSummaryRows
				recurringValue={null}
				totalInvested={totalInvested}
				totalValue={totalValue}
			/>
		</>
	);
};
